import React, { useState, useEffect } from 'react';
import { Search, X, Loader } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../utils/LanguageContext';
import { Link } from 'react-router-dom';

const API_BASE_URL = 'https://texla.uz/api/v1';

const getLocalizedText = (obj, language) => {
    if (!obj) return '';
    const key = `title_${language}`;
    return obj[key] || obj.title_uz || obj.title || '';
};

const getLocalizedProductName = (product, language) => {
    if (!product) return '';
    const key = `name_${language}`;
    return product[key] || product.name_uz || product.name || '';
};

const SearchBox = ({ isMobile = false }) => {
    const { t, language } = useLanguage();
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);

    // Search AJAX
    useEffect(() => {
        if (searchQuery.trim().length > 1) {
            performSearch();
        } else {
            setResults([]);
            setShowResults(false);
        }
    }, [searchQuery, language]);

    const performSearch = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/products/`, {
                params: {
                    search: searchQuery
                }
            });

            const searchResults = (response.data || [])
                .slice(0, 8)
                .map(product => ({
                    id: product.id,
                    name: getLocalizedProductName(product, language),
                    code: product.code,
                    image: product.main_image || 'https://via.placeholder.com/80',
                    brand: product.brands?.[0]?.[`title_${language}`] || product.brands?.[0]?.title_uz
                }));

            setResults(searchResults);
            setShowResults(true);
        } catch (error) {
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setSearchQuery('');
        setResults([]);
        setShowResults(false);
    };

    return (
        <div className={`relative ${isMobile ? 'w-full' : 'flex-1 min-w-[200px] max-w-sm lg:max-w-xl'}`}>
            <form
                onSubmit={(e) => e.preventDefault()}
                className="flex gap-0"
            >
                <input
                    type="text"
                    placeholder={t('header.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.trim().length > 1 && setShowResults(true)}
                    className={`w-full px-3 lg:px-4 py-2 pr-10 lg:pr-12 border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-transparent outline-none text-sm lg:text-base ${
                        isMobile ? 'rounded-lg' : 'rounded-l-lg'
                    }`}
                />
                {searchQuery && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <X size={16} />
                    </button>
                )}
                <button
                    type="button"
                    className={`px-2 lg:px-4 bg-blue-600 hover:bg-blue-700 transition flex items-center justify-center ${
                        isMobile ? 'rounded-lg ml-2' : 'rounded-r-lg'
                    }`}
                >
                    {loading ? (
                        <Loader size={18} className="text-white animate-spin" />
                    ) : (
                        <Search size={18} className="text-white" />
                    )}
                </button>
            </form>

            {/* Search Results Dropdown */}
            {showResults && results.length > 0 && (
                <div className={`absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 ${
                    isMobile ? 'w-full' : ''
                }`}>
                    <div className="max-h-96 overflow-y-auto">
                        {results.map(product => (
                            <Link
                                key={product.id}
                                to={`/product/${product.id}`}
                                onClick={() => {
                                    handleClear();
                                    window.scrollTo(0, 0);
                                }}
                                className="flex items-center gap-3 p-3 hover:bg-gray-50 border-b last:border-b-0 transition"
                            >
                                <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-12 h-12 object-cover rounded bg-gray-100"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 line-clamp-1">
                                        {product.name}
                                    </p>
                                    {product.brand && (
                                        <p className="text-xs text-gray-500">
                                            {product.brand}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-400">
                                        {product.code}
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Empty Results */}
            {showResults && results.length === 0 && !loading && searchQuery.trim().length > 1 && (
                <div className={`absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 text-center ${
                    isMobile ? 'w-full' : ''
                }`}>
                    <p className="text-sm text-gray-500">{t('shop.notFound')}</p>
                </div>
            )}
        </div>
    );
};

export default SearchBox;